/**
 * The confirmation, as the owner approved it on 21 September 2026 — `C2-Modal-Wyslano.dc.html`.
 *
 * It is a picture of a connection rather than a receipt: two monograms, a line drawn between them,
 * and then, once it has landed, the three things a trainer can usefully do in the minutes before
 * anybody answers. The dialog does not swap for it — the SAME element widens from 560px to 980px,
 * which is why this is a face and not a second popup (see `InviteClientDialog`).
 *
 * ── The sequence is one duration and fourteen sets of percentages ─────────────────────────────
 * Every movement here is a CSS keyframe on `--dur-invite-sequence` whose ramp sits at its own pair
 * of stops, because `tokens/base.css`'s single reduced-motion block collapses a DURATION and not a
 * DELAY. The score, its arithmetic and the one animation that keeps a real delay are worked through
 * in `InviteSentFace.module.css`.
 *
 * ── Everything this face claims is backed, and half of it only on ONE of the two paths ───────
 * `POST /customer/invite` opens a roster entry `Invited` and issues an offer that expires in 14
 * days (`InvitationLifetime`), so the timeline's "Czeka na odpowiedź · ważne 14 dni" and "Start
 * współpracy · po przyjęciu" are the contract rather than a promise about it (R4, R7). Nothing is
 * activated until the person accepts.
 *
 * What differs is WHO held the address. A free one is provisioned and the controller then opens the
 * intake from this trainer's template; an address that already has an account is only asked, so no
 * intake exists and the trainer has no coaching relation to that row yet. `model/sentInvitation`
 * reads that off `hasOpenEngagement` and answers which cards may be drawn; the sentence under the
 * heading follows the same branch.
 */

import { Dialog } from '@base-ui/react/dialog';
import { Link } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';

import type { SeatUsage } from '~/entities/client';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import { CheckIcon, WarningIcon } from '~/shared/ui/AuthIcons';
import { MeasureMarkIcon, PlanMarkIcon } from '~/shared/ui/EmptyStateIcons';
import brand from '~/shared/ui/brandMark.module.css';
import ink from '~/shared/ui/inkButton.module.css';

import { inviteMonograms, type Namesake } from '../model/monogram';
import { sentFaceCards, type SentInvitation } from '../model/sentInvitation';
import { ArrowRightIcon, VideoIcon } from './InviteGlyphs';
import styles from './InviteClientDialog.module.css';

interface InviteSentFaceProps {
  readonly sent: SentInvitation;
  /** The seats as they stand AFTER this invitation, frozen at the write. `null` while unknown. */
  readonly seatsAfter: SeatUsage | null;
  /** The host's warning about what the write did not do, drawn under the body. `null`: none. */
  readonly note: string | null;
  /** The signed-in trainer, for the left monogram. `null` draws the brand mark instead. */
  readonly trainer: Namesake | null;
  /**
   * Where focus lands when this face replaces the form.
   *
   * The dialog owns it because the element it has to move focus AWAY from — the submit button —
   * is the dialog's, and the move has to happen on the same commit that swaps the two.
   */
  readonly headingRef: React.RefObject<HTMLHeadingElement | null>;
  /**
   * Back to an empty form. The dialog narrows again on the same transition it widened on. Absent:
   * the host brought one person to invite (a lead's conversion) and there is no next one to offer.
   */
  readonly onAgain?: (() => void) | undefined;
  readonly onClose: () => void;
}

const KEY = 'web:clients.inviteDialog.sent';

export function InviteSentFace({
  sent,
  seatsAfter,
  note,
  trainer,
  headingRef,
  onAgain,
  onClose,
}: InviteSentFaceProps) {
  const { t, i18n } = useTranslation();
  const monograms = inviteMonograms(trainer, sent);
  const cards = new Set(sentFaceCards(sent));

  // `Intl` rather than the mobile app's static PL/EN tables, which exist only because Hermes throws
  // on a `timeZone` — and through `intlCache`, which is where this app builds its formatters. The
  // moment is frozen at the write, so this formats a fixed instant and cannot tick under a trainer
  // who is reading the page.
  const time = dateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(
    sent.at,
  );

  return (
    <div className={styles.sent}>
      <div className={styles.sentLeft}>
        {/* ── The picture, as ONE thing ──────────────────────────────────────────────────────
          * `role="img"` collapses the whole subtree, which is exactly right here: the letters, the
          * line, the travelling dot and the tick are a drawing of "you and this person", and read
          * out piecemeal they are four fragments saying nothing. The NAME carries the shape. */}
        <div
          className={styles.pair}
          role="img"
          aria-label={t(`${KEY}.connection`, { name: sent.firstName })}
        >
          <span className={`${styles.avatar} ${styles.avatarYou}`}>
            {monograms.trainer ?? (
              <span className={`${brand.brandMark} ${styles.avatarMark}`} />
            )}
            <span className={styles.avatarTag}>{t(`${KEY}.you`)}</span>
          </span>

          <span className={styles.line}>
            <span className={styles.lineBase} />
            <span className={styles.lineDot} />
          </span>

          <span className={`${styles.avatar} ${styles.avatarThem}`}>
            {monograms.client}
            <span className={styles.avatarTag}>{sent.firstName}</span>
            <span className={styles.avatarCheck}>
              <CheckIcon className={styles.avatarCheckMark} />
            </span>
          </span>
        </div>

        <div className={styles.headingRise}>
          <Dialog.Title className={styles.sentHeading} ref={headingRef} tabIndex={-1}>
            {t(`${KEY}.heading`)}
          </Dialog.Title>
        </div>

        {/* Two sentences, because two different things happened. The owner's approved copy tells
          * the trainer their invitation is on its way to an address; it is wrong for a person who
          * already has a MoveBuddy account, whose app is where the offer is waiting. That case
          * takes the MOBILE app's own words verbatim — `clients.invite.sentPanel.existingBody`,
          * out of the copied bundle — so a trainer who has met this on the phone reads the same
          * sentence here. No `<Trans>`: it interpolates nothing and emphasises nothing. */}
        <Dialog.Description className={styles.sentBody}>
          {sent.existingAccount ? (
            t('clients.invite.sentPanel.existingBody')
          ) : (
            <Trans
              i18nKey={`${KEY}.body`}
              values={{ email: sent.email }}
              components={{ b: <b className={styles.strong} /> }}
            />
          )}
        </Dialog.Description>

        {note === null ? null : (
          <p className={`${styles.notice} ${styles.sentNote}`}>
            <WarningIcon className={styles.noticeIcon} />
            <span>{note}</span>
          </p>
        )}

        {/* `role="list"` because `list-style: none` makes WebKit drop the list semantics
          * altogether, and this is a sequence whose length is part of what it says. */}
        <ol className={styles.timeline} role="list">
          <li className={styles.step}>
            <span className={`${styles.stepMark} ${styles.stepMarkDone}`} aria-hidden>
              <CheckIcon className={styles.stepCheck} />
            </span>
            <span className={styles.stepLabel}>{t(`${KEY}.timeline.sent`)}</span>
            <span className={styles.stepWhen}>{t(`${KEY}.timeline.sentAt`, { time })}</span>
          </li>
          <li className={styles.step}>
            <span className={`${styles.stepMark} ${styles.stepMarkNow}`} aria-hidden>
              <span className={styles.stepDot} />
            </span>
            <span className={styles.stepLabel}>{t(`${KEY}.timeline.waiting`)}</span>
            <span className={styles.stepWhen}>{t(`${KEY}.timeline.waitingFor`)}</span>
          </li>
          <li className={styles.step}>
            <span className={`${styles.stepMark} ${styles.stepMarkTodo}`} aria-hidden />
            <span className={styles.stepLabel}>{t(`${KEY}.timeline.start`)}</span>
            <span className={styles.stepWhen}>{t(`${KEY}.timeline.startWhen`)}</span>
          </li>
        </ol>
      </div>

      <div className={styles.sentRight}>
        <span className={styles.prepEyebrow}>{t(`${KEY}.prepEyebrow`)}</span>
        <h3 className={styles.prepHeading}>{t(`${KEY}.prepHeading`)}</h3>

        <div className={styles.cards}>
          {/* Which cards this invitation earns is `model/sentInvitation`'s decision, not this
            * component's — two of the four are claims about work the endpoint only does on one of
            * its two paths. All four are asked the same way so no rule about them lives here.
            *
            * The plan card carries the ONE accent on the set, and it marks the act a trainer most
            * often takes next. The other chips are neutral, which is what keeps it legible as a
            * choice rather than as decoration. */}
          {cards.has('plan') && (
            <Link to="/plans" className={`${styles.card} ${styles.cardHot}`} onClick={onClose}>
              <span className={styles.cardIcon}>
                <PlanMarkIcon className={styles.cardGlyph} />
              </span>
              <span className={styles.cardText}>
                <span className={styles.cardTitle}>{t(`${KEY}.plan.title`)}</span>
                <span className={styles.cardBody}>{t(`${KEY}.plan.body`)}</span>
              </span>
              <ArrowRightIcon className={styles.cardGo} />
            </Link>
          )}

          {/* A STATEMENT, not an act — and drawn only when an intake was actually opened.
            * „Podgląd" is quiet text rather than a link because there is nowhere honest to send
            * the trainer: the response carries no intake template id, and no requirement names a
            * destination. It led to `/raporty`, which is the catalogue of every survey and not
            * this client's. See AGENTS.md — the destination is an open question for the owner. */}
          {cards.has('survey') && (
            <div className={`${styles.card} ${styles.cardDone}`}>
              <span className={styles.cardIcon}>
                <CheckIcon className={styles.cardGlyph} />
              </span>
              <span className={styles.cardText}>
                <span className={styles.cardTitle}>{t(`${KEY}.survey.title`)}</span>
                <span className={styles.cardBody}>{t(`${KEY}.survey.body`)}</span>
              </span>
              <span className={styles.cardPeek}>{t(`${KEY}.survey.action`)}</span>
            </div>
          )}

          {/* Only on the path that provisioned the client. `AuthorizeCustomerDetailAccessAsync`
            * passes on a CURRENT coaching relation, which a fresh invite has and an offer to an
            * existing account does not — and `id` is optional on the wire besides.
            * `sentFaceCards` owns both conditions. */}
          {cards.has('measurements') && sent.clientId !== null && (
            <Link
              to="/klienci/$clientId"
              params={{ clientId: String(sent.clientId) }}
              search={{ tab: 'body' as const }}
              className={styles.card}
              onClick={onClose}
            >
              <span className={styles.cardIcon}>
                <MeasureMarkIcon className={styles.cardGlyph} />
              </span>
              <span className={styles.cardText}>
                <span className={styles.cardTitle}>{t(`${KEY}.measurements.title`)}</span>
                <span className={styles.cardBody}>{t(`${KEY}.measurements.body`)}</span>
              </span>
              <ArrowRightIcon className={styles.cardGo} />
            </Link>
          )}

          {/* A `<div>`, not a link and not a disabled button: there is nothing behind it to reach,
            * on this platform, today. A focusable stop that leads nowhere is a dead affordance —
            * the view ADR's "a refusal that carries a reason stays focusable" is about a control
            * that WOULD do something, which this is not. The chip says why in words. */}
          {cards.has('soon') && (
            <div className={`${styles.card} ${styles.cardSoon}`}>
              <span className={styles.cardIcon}>
                <VideoIcon className={styles.cardGlyph} />
              </span>
              <span className={styles.cardText}>
                <span className={styles.cardTitle}>{t(`${KEY}.soon.title`)}</span>
                <span className={styles.cardBody}>{t(`${KEY}.soon.body`)}</span>
              </span>
              <span className={styles.cardSoonChip}>{t(`${KEY}.soon.chip`)}</span>
            </div>
          )}
        </div>

        <div className={styles.sentFoot}>
          {/* Nothing at all on an unlimited plan — the same cut the form face makes, for the same
            * reason: a sentence whose subject is a limit has no subject when there is no limit. */}
          {seatsAfter !== null && !seatsAfter.unlimited && (
            <span className={styles.sentSeats}>
              <Trans
                i18nKey={`${KEY}.seatLine`}
                values={{ taken: seatsAfter.taken, cap: seatsAfter.cap }}
                components={{ b: <b className={styles.strong} /> }}
              />
            </span>
          )}
          <div className={styles.sentActions}>
            {onAgain === undefined ? null : (
              <button
                type="button"
                className={`${styles.ghost} ${styles.sentGhost}`}
                onClick={onAgain}
              >
                {t('web:clients.inviteDialog.done.again')}
              </button>
            )}
            <button type="button" className={`${ink.ink} ${styles.sentInk}`} onClick={onClose}>
              {t('web:clients.inviteDialog.done.back')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
